import { useState, useEffect } from 'react'
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock, Database, Activity, Play, Zap, Grid3X3 } from 'lucide-react'
import { adminApi, JobSyncLog, JobHealthSummary, DatabaseStats, JobConfigMatrix, JobConfig } from '../api/client'
import clsx from 'clsx'

function formatTimeAgo(timestamp: string | null): string {
  if (!timestamp) return 'Never'
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function StatusBadge({ status }: { status: string | null }) {
  const config = {
    SUCCESS: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    PARTIAL: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    FAILED: { color: 'bg-red-100 text-red-800', icon: XCircle },
    RATE_LIMITED: { color: 'bg-orange-100 text-orange-800', icon: Clock },
    TIMEOUT: { color: 'bg-gray-100 text-gray-800', icon: Clock },
  }[status || ''] || { color: 'bg-gray-100 text-gray-600', icon: Clock }

  const Icon = config.icon

  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium', config.color)}>
      <Icon className="w-3 h-3" />
      {status || 'Unknown'}
    </span>
  )
}

function HealthCard({ health }: { health: JobHealthSummary | null }) {
  if (!health) return null

  const successPercent = (health.successRate * 100).toFixed(1)

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-primary-600" />
        System Health
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">{health.totalJobs}</div>
          <div className="text-sm text-gray-500">Total Jobs</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600">{health.healthyJobs}</div>
          <div className="text-sm text-gray-500">Healthy</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-red-600">{health.failingJobs}</div>
          <div className="text-sm text-gray-500">Failing</div>
        </div>
        <div className="text-center">
          <div className={clsx(
            'text-3xl font-bold',
            parseFloat(successPercent) >= 99 ? 'text-green-600' :
            parseFloat(successPercent) >= 95 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {successPercent}%
          </div>
          <div className="text-sm text-gray-500">Success Rate</div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Syncs Today:</span>{' '}
          <span className="font-medium">{health.totalSyncsToday}</span>
        </div>
        <div>
          <span className="text-gray-500">Failures Today:</span>{' '}
          <span className="font-medium text-red-600">{health.totalFailuresToday}</span>
        </div>
      </div>
    </div>
  )
}

function DatabaseCard({ stats }: { stats: DatabaseStats | null }) {
  if (!stats) return null

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Database className="w-5 h-5 text-primary-600" />
        Database Stats
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">{stats.totalGames.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Games</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600">{stats.liveGames}</div>
          <div className="text-sm text-gray-500">Live Now</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">{stats.totalOdds.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Odds</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">{stats.totalStandings}</div>
          <div className="text-sm text-gray-500">Standings</div>
        </div>
      </div>
      {Object.keys(stats.gamesBySport).length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-sm text-gray-500 mb-2">Games by Sport:</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.gamesBySport).map(([sport, count]) => (
              <span key={sport} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                {sport}: {count.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface JobsTableProps {
  jobs: JobSyncLog[]
  onTriggerSync: (sport: string, dataType: string) => void
  syncingJobs: Set<string>
}

function JobsTable({ jobs, onTriggerSync, syncingJobs }: JobsTableProps) {
  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No jobs found
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sport</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Today</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {jobs.map((job) => {
            const jobKey = `${job.sport}-${job.dataType}`
            const isSyncing = syncingJobs.has(jobKey)

            return (
              <tr key={job.id} className={clsx(
                job.consecutiveFailures && job.consecutiveFailures >= 3 && 'bg-red-50'
              )}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {job.providerName}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {job.sport}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {job.dataType}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <StatusBadge status={job.lastStatus} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {formatTimeAgo(job.lastSuccessfulSync)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {job.recordsProcessed ?? '-'}
                  {job.recordsCreated !== null && job.recordsUpdated !== null && (
                    <span className="text-xs text-gray-400 ml-1">
                      (+{job.recordsCreated}/{job.recordsUpdated})
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <span className="text-green-600">{job.totalSyncsToday ?? 0}</span>
                  {(job.totalFailuresToday ?? 0) > 0 && (
                    <span className="text-red-600 ml-1">/ {job.totalFailuresToday} fail</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => onTriggerSync(job.sport, job.dataType)}
                    disabled={isSyncing}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100 disabled:opacity-50 transition-colors"
                  >
                    {isSyncing ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Play className="w-3 h-3" />
                    )}
                    {isSyncing ? 'Syncing...' : 'Sync'}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const DATA_TYPES = ['LIVE_SCORES', 'GAME_SCHEDULE', 'STANDINGS', 'ODDS'] as const

interface ConfigMatrixProps {
  config: JobConfigMatrix | null
  onTriggerSync: (sport: string, dataType: string) => void
  syncingJobs: Set<string>
}

function ConfigMatrix({ config, onTriggerSync, syncingJobs }: ConfigMatrixProps) {
  if (!config) return null

  // Group jobs by sport
  const jobsBySport = config.jobs.reduce((acc, job) => {
    if (!acc[job.sport]) {
      acc[job.sport] = { displayName: job.sportDisplayName, jobs: {} }
    }
    acc[job.sport].jobs[job.dataType] = job
    return acc
  }, {} as Record<string, { displayName: string, jobs: Record<string, JobConfig> }>)

  // Get unique sports that have at least one job
  const sports = Object.keys(jobsBySport).sort((a, b) => {
    // Sort by display name
    return jobsBySport[a].displayName.localeCompare(jobsBySport[b].displayName)
  })

  const getStatusIcon = (job: JobConfig | undefined) => {
    if (!job) return <span className="text-gray-300">-</span>
    if (!job.hasRun) return <Clock className="w-4 h-4 text-gray-400" />
    if (job.healthy && job.lastStatus === 'SUCCESS') return <CheckCircle className="w-4 h-4 text-green-500" />
    if (job.lastStatus === 'PARTIAL') return <AlertTriangle className="w-4 h-4 text-yellow-500" />
    if (job.lastStatus === 'RATE_LIMITED') return <Clock className="w-4 h-4 text-orange-500" />
    return <XCircle className="w-4 h-4 text-red-500" />
  }

  const getStatusTooltip = (job: JobConfig | undefined) => {
    if (!job) return 'Not supported'
    if (!job.hasRun) return 'Never run - click to sync'
    return `${job.lastStatus} - Last: ${formatTimeAgo(job.lastSuccessfulSync)}`
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
              Sport
            </th>
            {DATA_TYPES.map(dt => (
              <th key={dt} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dt.replace('_', ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sports.map(sport => {
            const sportData = jobsBySport[sport]
            return (
              <tr key={sport} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                  <div className="flex flex-col">
                    <span>{sportData.displayName}</span>
                    <span className="text-xs text-gray-400">{sport}</span>
                  </div>
                </td>
                {DATA_TYPES.map(dt => {
                  const job = sportData.jobs[dt]
                  const jobKey = `${sport}-${dt}`
                  const isSyncing = syncingJobs.has(jobKey)
                  const isSupported = !!job

                  return (
                    <td key={dt} className="px-4 py-3 text-center">
                      {isSupported ? (
                        <button
                          onClick={() => onTriggerSync(sport, dt)}
                          disabled={isSyncing}
                          title={getStatusTooltip(job)}
                          className={clsx(
                            'inline-flex items-center justify-center gap-1 p-2 rounded-lg transition-colors',
                            isSyncing && 'opacity-50',
                            job.hasRun && job.healthy ? 'hover:bg-green-50' : 'hover:bg-gray-100'
                          )}
                        >
                          {isSyncing ? (
                            <RefreshCw className="w-4 h-4 animate-spin text-primary-600" />
                          ) : (
                            getStatusIcon(job)
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="mt-4 px-4 pb-4 flex flex-wrap gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Healthy</span>
        <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-yellow-500" /> Partial</span>
        <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> Failed</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-orange-500" /> Rate Limited</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-gray-400" /> Never Run</span>
        <span className="flex items-center gap-1"><span className="text-gray-300">-</span> Not Supported</span>
      </div>
    </div>
  )
}

export default function AdminPage() {
  const [health, setHealth] = useState<JobHealthSummary | null>(null)
  const [dbStats, setDbStats] = useState<DatabaseStats | null>(null)
  const [jobs, setJobs] = useState<JobSyncLog[]>([])
  const [jobConfig, setJobConfig] = useState<JobConfigMatrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [syncingJobs, setSyncingJobs] = useState<Set<string>>(new Set())
  const [syncingAll, setSyncingAll] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'matrix' | 'table'>('matrix')

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [healthData, jobsData, statsData, configData] = await Promise.all([
        adminApi.getJobHealth(),
        adminApi.getAllJobs(),
        adminApi.getDatabaseStats().catch(() => null),
        adminApi.getJobConfigs().catch(() => null),
      ])
      setHealth(healthData)
      setJobs(jobsData)
      setDbStats(statsData)
      setJobConfig(configData)
      setLastRefresh(new Date())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch admin data')
    } finally {
      setLoading(false)
    }
  }

  const triggerSync = async (sport: string, dataType: string) => {
    const jobKey = `${sport}-${dataType}`
    setSyncingJobs(prev => new Set(prev).add(jobKey))
    setSyncMessage(null)

    try {
      let result
      if (dataType === 'LIVE_SCORES') {
        result = await adminApi.triggerLiveSync(sport)
      } else if (dataType === 'GAME_SCHEDULE') {
        result = await adminApi.triggerScheduleSync(sport)
      } else if (dataType === 'STANDINGS') {
        result = await adminApi.triggerStandingsSync(sport)
      } else if (dataType === 'ODDS') {
        result = await adminApi.triggerOddsSync(sport)
      }

      if (result) {
        setSyncMessage(`${sport} ${dataType}: ${result.processed} processed, ${result.created} created, ${result.updated} updated`)
      }

      // Refresh data after sync
      await fetchData()
    } catch (err) {
      setSyncMessage(`Error syncing ${sport} ${dataType}: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setSyncingJobs(prev => {
        const next = new Set(prev)
        next.delete(jobKey)
        return next
      })
    }
  }

  const triggerSyncAll = async () => {
    setSyncingAll(true)
    setSyncMessage(null)

    try {
      const result = await adminApi.triggerSyncAll()
      const totalLive = result.liveScores.reduce((sum, r) => sum + r.processed, 0)
      const totalSchedule = result.schedules.reduce((sum, r) => sum + r.processed, 0)
      setSyncMessage(`Sync All complete: ${totalLive} live scores, ${totalSchedule} scheduled games processed`)

      // Refresh data after sync
      await fetchData()
    } catch (err) {
      setSyncMessage(`Error syncing all: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setSyncingAll(false)
    }
  }

  useEffect(() => {
    fetchData()
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500">
            Monitor system health, jobs, and database statistics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={triggerSyncAll}
            disabled={syncingAll}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {syncingAll ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            {syncingAll ? 'Syncing...' : 'Sync All'}
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Last refresh time */}
      <div className="text-xs text-gray-400">
        Last updated: {lastRefresh.toLocaleTimeString()}
      </div>

      {/* Sync message */}
      {syncMessage && (
        <div className={clsx(
          'rounded-lg p-4 text-sm',
          syncMessage.includes('Error') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
        )}>
          {syncMessage}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Health and Database Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HealthCard health={health} />
        <DatabaseCard stats={dbStats} />
      </div>

      {/* Jobs Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-primary-600" />
              Sync Jobs
            </h2>
            <p className="text-sm text-gray-500">
              {jobConfig ? `${jobConfig.totalConfigs} supported configurations, ${jobConfig.configsWithHistory} have run` : `${jobs.length} jobs with history`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('matrix')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                viewMode === 'matrix' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              Matrix
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={clsx(
                'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                viewMode === 'table' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              Table
            </button>
          </div>
        </div>
        {viewMode === 'matrix' ? (
          <ConfigMatrix config={jobConfig} onTriggerSync={triggerSync} syncingJobs={syncingJobs} />
        ) : (
          <JobsTable jobs={jobs} onTriggerSync={triggerSync} syncingJobs={syncingJobs} />
        )}
      </div>
    </div>
  )
}
